import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { captureMessage } from '@sentry/react';
import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import moment from 'moment';
import currency from 'currency.js';
import Pagination from "react-bootstrap/Pagination";
import { asyncDebouncer, exportCSVFile } from '../../utils/helper';
import DecimalInput from '../../common/DecimalInput';
import useDidMountEffect from '../../utils/useDidMountEffect';
import PAlert from '../../common/PAlert';

const perPage = 50;

const sortingStyle = {
    color: 'rgb(0, 151, 254)',
    cursor: 'pointer',
    marginLeft: '8px',
};

const Sorting = ({ sort = 'ASC' }) => {
    return sort === 'ASC'
        ? <FontAwesomeIcon
            icon={["fas", "chevron-up"]}
            style={sortingStyle}
        />
        : <FontAwesomeIcon
            icon={["fas", "chevron-down"]}
            style={sortingStyle}
        />;
};

const tableHeaders = [
    {
        name: 'Tag',
        sortable: false,
        column: '',
        width: '20px',
    },
    {
        name: 'Username',
        sortable: true,
        column: 'login',
        width: '20%',
    },
    {
        name: 'Email',
        sortable: true,
        column: 'email',
        width: '25%',
    },
    {
        name: 'User ID',
        sortable: true,
        column: 'id',
        width: '',
    },
    {
        name: 'First Payment',
        sortable: true,
        column: 'first_subscription_date',
        width: '10%',
    },
    {
        name: 'Last Payment',
        sortable: true,
        column: 'last_payment_date',
        width: '10%',
    },
    {
        name: 'Total Spend',
        sortable: true,
        column: 'total_payments',
        width: '',
    },
    {
        name: 'PUs',
        sortable: true,
        column: 'total_followed',
        width: '3%',
    },
];

const SuList = ({
    selectedSus,
    setSelectedSus,
    fromRange,
    toRange,
    setRefetch,
    refetch,
    spend,
    setIsBusy,
    excluded,
    selectedPus,
    suToInclude,
    setSuToInclude,
    selectedSUsObject,
    setSelectedSUsObject,
    setMainList,
    mainList,
    puFollowersToInclude,
    setPuFollowersToInclude,
    removeCount,
    firstSubscription
}) => {
    const [sus, setSus] = useState([]);
    const [gettingSuList, setGettingSuList] = useState(false);
    const [page, setPage] = useState(1);
    const [orderBy, setOrderBy] = useState('login');
    const [sortBy, setSortBy] = useState('ASC');
    const [total, setTotal] = useState(0);
    const [pageItems, setPageItems] = useState(0);
    const [tagAll, setTagAll] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertTitle, setAlertTitle] = useState(false);
    const [alertBody, setAlertBody] = useState('');

    const handleClearAll = () => {
        setSelectedSus([]);
        setSelectedSUsObject([]);
    };

    const handleTagAll = () => {
        setTagAll(true);
    };

    const getSus = asyncDebouncer(async (reset = false) => {
        setGettingSuList(true);
        let from = '';
        let to = '';

        if (fromRange && toRange) {
            from = moment.utc(fromRange).local().format('YYYY-MM-DD');
            to = moment.utc(toRange).local().format('YYYY-MM-DD');
        }

        if (reset) {
            setPage(1);
        }

        let sus = [];
        let pus = [];

        if (suToInclude.length > 0) {
            sus = suToInclude.map(su => su.name);
        }
        if (puFollowersToInclude.length > 0) {
            pus = puFollowersToInclude.map(pu => pu.id);
        }

        let notIncluded = excluded.split('\n').map(e => {
            const suData = e.split(',');

            return suData[0];
        });

        if (mainList.length > 0) {
            if (notIncluded.length > 0) {
                notIncluded = notIncluded.join(',').concat(',');
            }
            notIncluded = notIncluded.concat(mainList.map(su => su.id).join(','));
        } else {
            notIncluded = notIncluded.join(',');
        }

        await axios.post('/api/administration/creator-sales-boost/sus', {
            tag_all: tagAll,
            exclude: notIncluded,
            page: reset ? 1 : page,
            per_page: perPage,
            order_by: orderBy,
            sort_by: sortBy,
            from_range: from,
            to_range: to,
            pu_id: selectedPus.length > 0 ? selectedPus[0].id : '',
            first_subscription: firstSubscription,
            spend,
            sus,
            pus
        })
            .then(response => {
                if (tagAll) {
                    const all = response.data.map(su => su.id);

                    setSelectedSus([...new Set([
                        ...selectedSus,
                        ...all
                    ])]);

                    let tmpObj = [
                        ...new Map([
                            ...selectedSUsObject,
                            ...response.data
                        ].map((item) => [item["id"], item]))
                    ];

                    tmpObj = new Map(tmpObj);

                    setSelectedSUsObject(
                        Array.from(tmpObj.values())
                    );

                    setTagAll(false);
                } else {
                    setSus(response.data);
                    setTotal(response.data.total);
                    setPageItems(Math.ceil(response.data.total / perPage));
                }

                setGettingSuList(false);
            })
            .catch(error => {
                setGettingSuList(false);
                captureMessage(error);
            });
    }, 600);

    useDidMountEffect(() => {
        getSus();
    }, [page]);

    useDidMountEffect(() => {
        getSus(true);
    }, [sortBy, orderBy, refetch]);

    useDidMountEffect(() => {
        setIsBusy(gettingSuList);
    }, [gettingSuList]);


    const handleSorting = (e, column) => {
        e.preventDefault();

        if (orderBy === column) {
            if (sortBy === 'ASC') {
                setSortBy('DESC');
            } else {
                setSortBy('ASC');
            }
        } else {
            setOrderBy(column);
            setSortBy('ASC');
        }

        setPage(1);
    };

    const handleSelectSu = (su) => {
        let tmpSelectedSus = [...selectedSus];
        let tmpObj = [...selectedSUsObject];

        const index = tmpSelectedSus.indexOf(su.id);

        if (index >= 0) {
            tmpSelectedSus.splice(index, 1);
            tmpObj.splice(index, 1);
        } else {
            tmpSelectedSus.push(su.id);
            tmpObj.push(su);
        }

        setSelectedSus(tmpSelectedSus);
        setSelectedSUsObject(tmpObj);
    };


    const handleNextPage = () => {
        if (sus.current_page < sus.last_page)
            setPage(sus.current_page + 1);
        // setRefetch(refetch => refetch + 1)
    };

    const handleLastPage = () => {
        setPage(sus.last_page);
        // setRefetch(refetch => refetch + 1)
    };

    const handlePrevPage = () => {
        if (sus.current_page > 1)
            setPage(sus.current_page - 1);
        // setRefetch(refetch => refetch + 1)
    };
    const handleFirstPage = () => {
        setPage(1);
        // setRefetch(fetch)
        // setRefetch(refetch => refetch + 1)
    };

    const PaginationContainer = () => {
        const isPrevDisabled = page > 1 ? false : true;
        const isNextDisabled = page < sus.last_page ? false : true;
        const handleManualPage = (value) => {
            let tmpValue = value;
            if (tmpValue.length > 0) {
                tmpValue = tmpValue.toString().replace(/[^0-9+]/g, '');
                if (tmpValue) {
                    tmpValue = parseInt(tmpValue, 10);
                }
            }

            setPage(tmpValue);
        };
        return (
            <Pagination
                style={{ alignItems: "center", marginBottom: 0 }}
            >
                <span className="mr-3">
                    {total} item{total > 1 ? "s" : ""}
                </span>
                <Pagination.First
                    disabled={isPrevDisabled}
                    className="mr-1"
                    onClick={handleFirstPage}
                />
                <Pagination.Prev
                    disabled={isPrevDisabled}
                    onClick={handlePrevPage}
                />
                <div className="mx-2 d-flex flex-row align-items-center justify-content-center w-auto">
                    <div className="mr-1" style={{ width: 35 }}>
                        <DecimalInput
                            inputMode="numeric"
                            className="pagination-input"
                            value={page}
                            handleChange={handleManualPage}
                            bannedCharacters={['e', 'E', '+', '-', '.']}
                        />
                    </div>
                    of {pageItems}
                </div>
                <Pagination.Next
                    disabled={isNextDisabled}
                    onClick={handleNextPage}
                />
                <Pagination.Last
                    disabled={isNextDisabled}
                    className="ml-1"
                    onClick={handleLastPage}
                />
            </Pagination>
        );
    };

    useDidMountEffect(() => {
        // if (tagAll && sus.data && sus.data.length > 0) {
        //     const all = sus.data.map(su => su.id)
        //     setSelectedSus([...new Set([
        //         ...selectedSus,
        //         ...all
        //     ])])

        //     let tmpObj = [
        //         ...new Map([
        //             ...selectedSUsObject,
        //             ...sus.data
        //         ].map((item) => [item["id"], item]))
        //     ]

        //     tmpObj = new Map(tmpObj)

        //     setSelectedSUsObject(
        //         Array.from(tmpObj.values())
        //     )
        // }
        // setTagAll(false)

        if (tagAll) {
            getSus();
        } else {
            if (!sus.data || sus.data.length <= 0) {
                getSus();
            }
        }
    }, [tagAll]);


    useDidMountEffect(() => {
        if (selectedSus.length <= 0) {
            setSelectedSUsObject([]);
        }
    }, [selectedSus]);

    useDidMountEffect(() => {
        if (selectedPus.length > 0 && sus.data && sus.data.length > 0) {
            setSelectedSUsObject([]);
            setSelectedSus([]);
            getSus();
        }
    }, [selectedPus]);

    const handleRemoveList = () => {
        getSus();
        // check selected SU in the working list
        const ids = [];
        const tmpObj = JSON.parse(JSON.stringify(selectedSUsObject));
        let excludeArray = excluded.split('\n');
        const excludedIds = excludeArray.map(su => {
            const suData = su.split(',');
            return suData[0];
        });
        excludeArray = tmpObj.filter(su => {
            if (excludedIds.indexOf(su.id) >= 0) {
                ids.push(su.id);
            }
            return excludedIds.indexOf(su.id) < 0;
        });
        setSelectedSUsObject(excludeArray);
        let tmpSelectedSus = [...selectedSus];
        tmpSelectedSus = tmpSelectedSus.filter(su => ids.indexOf(su) < 0);
        setSelectedSus(tmpSelectedSus);
    };

    useDidMountEffect(() => {
        handleRemoveList();
    }, [removeCount]);

    const handleSetMainList = () => {
        const existing = [...mainList];
        let selected = [...selectedSUsObject];

        if (!selected || selected.length <= 0) {
            return;
        }

        existing.forEach(data => {
            const index = selected.findIndex(obj => obj.id === data.id);
            selected.splice(index, 1);
        });

        let tmp = [
            ...existing,
            ...selected
        ];

        setMainList(tmp);

        setSelectedSus([]);
        setSelectedSUsObject([]);

        setRefetch(refetch => refetch + 1);
    };

    const handleExportTagged = () => {
        const headers = {
            id: 'User ID',
            login: 'Login',
            email: 'Email',
            created_date: 'Created Payment Date',
            total_followed: 'PUs Following',
            total_payments: 'Total Spend',
            last_payment_date: 'Last Payment Date',
            first_subscription_date: 'First Subscription Date'
        };
        const tmpSelected = [...selectedSUsObject];
        exportCSVFile(
            headers,
            tmpSelected,
            `CreatorSalesBoostWithSUs-${moment().format('YYYY-MM-DD')}`
        );
    };

    const handleExportSuFollowingPu = () => {
        if (puFollowersToInclude.length <= 0) {
            setShowAlert(true);
            setAlertBody('Please select at least 1 PU from the "Add SUs that are following this PU" field');
        } else {
            axios.get('/api/administration/creator-sales-boost/pus-sus', {
                params: {
                    pus: puFollowersToInclude.map(pu => pu.id),
                    except: selectedPus.length > 0 ? selectedPus[0].id : ''
                }
            })
                .then(response => {
                    if (response.data.length <= 0) {
                        setShowAlert(true);
                        setAlertBody('PU selected does not have followers');
                    } else {
                        const headers = {
                            id: 'User ID',
                            login: 'Login',
                            email: 'Email'
                        };
                        exportCSVFile(
                            headers,
                            response.data,
                            `PuSus-${moment().format('YYYY-MM-DD')}`
                        );
                    }
                })
                .catch(error => {
                    setShowAlert(true);
                    setAlertBody('Unable to export SUs');
                });
        }
    };

    return <React.Fragment>
        <div className="actions my-3">
            <button
                className="btn btn-primary"
                onClick={handleTagAll}
            >
                Tag All Users
            </button>
            <button
                className="btn btn-primary"
                onClick={handleClearAll}
            >
                Clear Tagged Selections
            </button>
            <button
                className="btn btn-primary"
                onClick={handleExportTagged}
            >
                Export Tagged
            </button>
            <button
                className="btn btn-primary"
                onClick={handleSetMainList}
            >
                Add Tagged Users To Main List
            </button>
            <button
                className="btn btn-primary"
                onClick={handleExportSuFollowingPu}
            >
                Export SUs Following PU
            </button>
        </div>
        {gettingSuList
            ? <div className='mt-4'><Spinner size="sm" variant='primary' animation='border' /> Fetching data...</div>
            : ''}
        <div className="d-flex flex-row justify-content-between align-items-center my-3">
            <div className="col-md-6">
                {total > 0 && PaginationContainer()}
            </div>
            <div className="col-md-6 text-right">
                <strong>{selectedSus.length} Selected SUs</strong>
            </div>
        </div>
        <div className="my-2">
            <strong>DRAFT FOLLOWER LIST</strong>
        </div>
        <table className="table table-sm table-bordered table-striped table-hover mt-2 su-list">
            <thead>
                <tr>
                    {
                        tableHeaders.map((header, index) => {
                            if (header.sortable) {
                                return <th key={`${header.column}-${index}`} width={header.width}>
                                    {orderBy === header.column
                                        ? <>
                                            <a
                                                href="#"
                                                onClick={(e) => handleSorting(e, header.column)}
                                                className="active"
                                            >
                                                {header.name}

                                                <Sorting sort={sortBy} setSortBy={setSortBy} />
                                            </a>
                                        </>
                                        : <>
                                            <a
                                                href="#"
                                                onClick={(e) => handleSorting(e, header.column)}
                                            >
                                                {header.name}
                                            </a>
                                        </>
                                    }
                                </th>;
                            }

                            return <th key={`${header.column}-${index}`} width={header.width}>{header.name}</th>;
                        })
                    }
                </tr>
            </thead>
            <tbody>
                {
                    sus && sus.data && sus.data.length > 0 && sus.data.map(su => {
                        return <tr key={su.id} onClick={() => handleSelectSu(su)}>
                            <td>
                                <input
                                    type="checkbox"
                                    value={su.id}
                                    onChange={(e) => handleSelectSu(su)}
                                    checked={selectedSus.indexOf(su.id) >= 0}
                                />
                            </td>
                            <td>
                                {su.login}
                            </td>
                            <td>
                                {su.email}
                            </td>
                            <td>
                                {su.id}
                            </td>
                            <td>
                                {su.first_subscription_date ? moment.utc(su.first_subscription_date).local().format('ll') : 'No Transaction'}
                            </td>
                            <td>
                                {su.last_payment_date ? moment.utc(su.last_payment_date).local().format('ll') : 'No Transaction'}
                            </td>
                            <td>
                                {currency(su.total_payments).format({ precision: 2 })}
                            </td>
                            <td>
                                {su.total_followed}
                            </td>
                        </tr>;
                    })
                }

                {
                    !sus || !sus.data || sus.data.length <= 0 && <tr>
                        <td colSpan="7" align='center'>
                            No SU found
                        </td>
                    </tr>
                }
            </tbody>
        </table>

        <PAlert show={showAlert} setShow={setShowAlert} title={alertTitle} body={alertBody} />
    </React.Fragment>;
};

export default React.memo(SuList);
